import { unzip, type UnzipFileInfo } from 'fflate';

/**
 * Drop in, upload-ready Files out. Returns a result or throws a typed error; it
 * decides NOTHING about how a rejection is displayed. develop's equivalent took
 * the notification service as a parameter to a size check (fileSizeCheck.ts:7) —
 * §5.1's subtler leak, since a cap check has no business owning presentation.
 *
 * D5: ONE flat cap. develop branched on EnvironmentSizeEnum between
 * MAX_TOTAL_SIZE_BYTES_SMALL and _LARGE, which are byte-identical
 * (fileSizeCheck.ts:3-4) — so the ternary picked between equal values. The BRANCH
 * is dead, not the cap; and the branch could not be ported regardless, because
 * EnvironmentSizeEnum lives in CustomAuthenticationService, which 3.13 does not
 * have. This remains a UX guard, not a security boundary — the backend is the
 * enforcement point.
 */
export const MAX_TOTAL_SIZE_BYTES = 1024 * 1024 * 1000;

export const bytesToMegabytes = (bytes: number): number => Math.round(bytes / (1024 * 1024));

export class IntakeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IntakeError';
    // Babel's class transform can break `instanceof` on Error subclasses; the same
    // one line http/exceptions.ts:24-26 carries, for the same reason.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * §5.3. A DENYLIST, deliberately: most DICOM files carry no extension at all,
 * which is almost certainly why develop's isDicomFile() degenerated to a hardcoded
 * `return true` (DicomUploadProcessor.ts:20-25) and why the parallel filter in its
 * hook degenerated the same way (useDicomUpload.ts:374-382). An allowlist of .dcm
 * would reject most real input.
 *
 * A module constant, not configuration. window.config.dicomUpload.acceptedExtensions
 * is typed as a flat array (AppTypes.ts:101-104) and written as a mime-to-extension
 * map in every deploy config — that disagreement is what got the original consumers
 * commented out, so a config-driven filter would reintroduce the bug. Neither key
 * is ported.
 */
const JUNK_DIRECTORIES = new Set(['__MACOSX']);
const JUNK_BASENAMES = new Set(
  ['.DS_Store', 'Thumbs.db', 'DICOMDIR'].map(basename => basename.toLowerCase())
);

/** PKZIP APPNOTE 4.4.5. fflate can inflate these two and nothing else. */
const STORED = 0;
const DEFLATE = 8;

function isJunkPath(path: string): boolean {
  if (path.endsWith('/')) {
    return true;
  }

  const segments = path.split('/');

  if (segments.some(segment => JUNK_DIRECTORIES.has(segment))) {
    return true;
  }

  const basename = segments[segments.length - 1];

  if (!basename) {
    return true;
  }

  // Plan D-6. macOS writes `._<name>` AppleDouble siblings both inside __MACOSX/
  // and, after some extraction paths, beside the real files. Under §6 defects 2
  // and 3 combined, each would get a fabricated 132-byte header and be POSTed as
  // DICOM — the failure the design calls its most consequential.
  return basename.startsWith('._') || JUNK_BASENAMES.has(basename.toLowerCase());
}

function isZipName(name: string): boolean {
  return name.toLowerCase().endsWith('.zip');
}

function unzipAsync(
  data: Uint8Array,
  filter: (info: UnzipFileInfo) => boolean
): Promise<Record<string, Uint8Array>> {
  return new Promise((resolve, reject) => {
    unzip(data, { filter }, (error, unzipped) => {
      if (error) {
        reject(new IntakeError(`Could not read the archive: ${error.message}`));
        return;
      }

      resolve(unzipped);
    });
  });
}

/**
 * Plan D-5. An entry whose compression method is neither 0 nor 8 aborts the ENTIRE
 * archive if the filter accepts it: fflate's unzip ends its per-entry branch with
 * `else cbl(err(14, 'unknown compression type ' + c), null)`, and cbl with an error
 * terminates every worker and delivers the error to the top-level callback. So one
 * bzip2 entry anywhere would make a whole valid study undroppable.
 */
function isReadable(info: UnzipFileInfo): boolean {
  return info.compression === STORED || info.compression === DEFLATE;
}

/**
 * Plan D-4, and the whole point of D4. The filter returns false for EVERY entry, so
 * fflate takes its `else cbl(null, null)` branch and decompresses nothing — the
 * callback receives {}. That is the only way to total `originalSize` before any
 * inflate, because fflate calls the filter for an entry and then immediately
 * inflates THAT entry: a single pass that summed as it went would have decompressed
 * everything up to the cap by the time it noticed, which is develop's own failure
 * (§6 defect 9) with a different library.
 */
async function measureArchive(
  data: Uint8Array
): Promise<{ totalOriginalSize: number; unreadable: number }> {
  let totalOriginalSize = 0;
  let unreadable = 0;

  await unzipAsync(data, (info: UnzipFileInfo) => {
    if (isJunkPath(info.name)) {
      return false;
    }

    if (!isReadable(info)) {
      unreadable += 1;
      return false;
    }

    totalOriginalSize += info.originalSize;
    return false;
  });

  return { totalOriginalSize, unreadable };
}

async function expandArchive(data: Uint8Array): Promise<File[]> {
  const unzipped = await unzipAsync(
    data,
    (info: UnzipFileInfo) => !isJunkPath(info.name) && isReadable(info)
  );

  return Object.entries(unzipped).map(([path, bytes]) => {
    const name = path.split('/').pop() ?? path;

    // §6 defect 13: develop produced a Blob via JSZip's async('blob') and then
    // wrapped it in a SECOND Blob before constructing the File
    // (DicomUploadProcessor.ts:39-42, :31-37), holding two full copies per entry.
    return new File([bytes], name);
  });
}

function assertUnderCap(totalBytes: number): void {
  if (totalBytes > MAX_TOTAL_SIZE_BYTES) {
    throw new IntakeError(
      `Total upload size must be less than ${bytesToMegabytes(MAX_TOTAL_SIZE_BYTES)}MB`
    );
  }
}

/**
 * Read once, measured and expanded from the same array. Holding every archive's
 * bytes before expanding any is deliberate — the cap must cover the whole batch,
 * so no entry may be decompressed until the last one is measured. §10 risk 4
 * accounts for the peak. Shared by expandDropped's top-level archives and its
 * depth-1 nested ones (plan D-12) — a nested archive is exactly as capable of
 * threatening memory as a top-level one, so it gets the same treatment.
 */
async function measureAndBuffer(
  archives: readonly File[]
): Promise<{ total: number; buffers: Uint8Array[]; unreadable: number }> {
  let total = 0;
  let unreadable = 0;
  const buffers: Uint8Array[] = [];

  for (const archive of archives) {
    const data = new Uint8Array(await archive.arrayBuffer());
    const measured = await measureArchive(data);

    if (measured.unreadable > 0) {
      // Logged as well as returned, the shape rollbackStudies uses: the count is
      // what the hook needs to raise a toast, the archive name is diagnosis and
      // has no place in one.
      console.error(
        `[sycai] ${measured.unreadable} entr(ies) in ${archive.name} use a compression ` +
          `method fflate cannot read; skipped`
      );
    }

    total += measured.totalOriginalSize;
    unreadable += measured.unreadable;
    buffers.push(data);
  }

  return { total, buffers, unreadable };
}

export interface IntakeResult {
  files: File[];
  /**
   * Entries dropped because fflate cannot inflate them (isReadable above). §5.1:
   * returned rather than logged and forgotten, because "the user must be told
   * instances went missing" is a presentation decision and this file makes none —
   * rollbackStudies is the same shape for the same reason. Silently shedding
   * entries under D3 means instances vanish from a study and the remainder
   * uploads as if it were complete.
   */
  unreadable: number;
}

/**
 * D3's invariant applies to the RESULT: one drop is one study. Nothing here
 * enforces that — it cannot be read off the bytes — but every consumer assumes it.
 */
export async function expandDropped(dropped: readonly File[]): Promise<IntakeResult> {
  const loose: File[] = [];
  const archives: File[] = [];

  for (const file of dropped) {
    // Verified against node_modules/file-selector/dist/file.js:19-38 (react-dropzone@10's
    // bundled file walker, the library behind DropZone.tsx's drag path): a folder DRAG
    // never sets webkitRelativePath -- that property is native-browser-only and is
    // populated solely by <input webkitdirectory>, which is openPicker's path in
    // DropZone.tsx, not react-dropzone's drag path. For a dropped directory,
    // file-selector instead defines its own non-standard `path` property from the
    // directory entry's fullPath, so a __MACOSX/ ancestor on a folder DRAG is only
    // visible through THAT. `path` is absent from the folder picker's native File
    // objects, so the chain still falls back to webkitRelativePath for that case, and
    // to `name` as the final, ancestor-less fallback for a plain file pick or drag.
    const relativePath = (file as { path?: string }).path || file.webkitRelativePath || file.name;

    if (isJunkPath(relativePath)) {
      continue;
    }

    (isZipName(file.name) ? archives : loose).push(file);
  }

  const looseTotal = loose.reduce((sum, file) => sum + file.size, 0);
  const {
    total: archivesTotal,
    buffers,
    unreadable: archivesUnreadable,
  } = await measureAndBuffer(archives);

  assertUnderCap(looseTotal + archivesTotal);

  const expanded: File[] = [...loose];

  for (const data of buffers) {
    // Not `expanded.push(...(await expandArchive(data)))`: spreading a direct await
    // expression crashes @babel/plugin-transform-regenerator ("unknown Expression of
    // type SpreadElement") under the `test` babel env, the same forced-regenerator
    // constraint useOrganReview.ts:150-161 documents for optional catch bindings.
    // Awaiting into a local first, then spreading that, transforms cleanly.
    const files = await expandArchive(data);
    expanded.push(...files);
  }

  const nested = expanded.filter(file => isZipName(file.name));

  if (nested.length === 0) {
    return { files: expanded, unreadable: archivesUnreadable };
  }

  // §5.3: depth 1. develop recursed unbounded (DicomUploadProcessor.ts:56-61).
  const flat = expanded.filter(file => !isZipName(file.name));

  /**
   * Plan D-12, amended by user ruling. The outer central directory reports only a
   * nested archive's COMPRESSED size, so its contents are invisible to the
   * assertUnderCap above -- the one hole the pre-expansion cap cannot see through
   * (§5.3). D-12 as originally written let a nested archive be fully decompressed
   * into File objects before re-checking the cap, accepting that as "one archive's
   * worth" of tolerated over-cap work; a nested study large enough to threaten the
   * cap is large enough to threaten the tab's memory the same way, so nested
   * archives get measureAndBuffer's measure-then-expand treatment too, not just a
   * post-hoc check of bytes already inflated.
   */
  const flatTotal = flat.reduce((sum, file) => sum + file.size, 0);
  const {
    total: nestedTotal,
    buffers: nestedBuffers,
    unreadable: nestedUnreadable,
  } = await measureAndBuffer(nested);

  assertUnderCap(flatTotal + nestedTotal);

  for (const data of nestedBuffers) {
    const inner = await expandArchive(data);
    flat.push(...inner.filter(file => !isZipName(file.name)));
  }

  // Backstop, not the primary guard now that measureAndBuffer runs first above: the
  // central directory's originalSize field is reported, not verified, so this checks
  // the ACTUAL inflated bytes in case it undercounted.
  assertUnderCap(flat.reduce((sum, file) => sum + file.size, 0));

  return { files: flat, unreadable: archivesUnreadable + nestedUnreadable };
}

const DICM = [0x44, 0x49, 0x43, 0x4d];
const PREAMBLE_LENGTH = 128;
const HEADER_LENGTH = PREAMBLE_LENGTH + DICM.length;

/**
 * §6 defect 8: four bytes, not develop's whole-file FileReader read to inspect four
 * (DicomValidator.ts:22-72).
 */
async function hasDicomMagic(file: File): Promise<boolean> {
  if (file.size < HEADER_LENGTH) {
    return false;
  }

  const head = new Uint8Array(await file.slice(PREAMBLE_LENGTH, HEADER_LENGTH).arrayBuffer());

  return DICM.every((byte, index) => head[index] === byte);
}

/**
 * D6. Check, and if absent prepend — and then upload, with no second validation.
 * develop validated the bytes it had just written (DicomUploader.ts:92-97), a check
 * that cannot fail, which advertises a gate that does not exist (§6 defect 3).
 *
 * OPEN QUESTION, recorded rather than resolved (D6, §7.2): prepending a preamble to
 * a raw dataset yields a MALFORMED Part 10 file — valid Part 10 needs a group-0002
 * header after DICM, not just the magic. If the backend accepts these it is
 * sniffing, and that must be confirmed against the real viewer API before this path
 * is treated as load-bearing.
 */
export async function ensurePreamble(file: File): Promise<File> {
  if (await hasDicomMagic(file)) {
    return file;
  }

  const header = new Uint8Array(HEADER_LENGTH);
  header.set(DICM, PREAMBLE_LENGTH);

  // One wrap. develop built a Blob for the header, a second Blob combining it with
  // the file, and then a File from that (DicomUploader.ts:67-73); File's
  // constructor takes BlobParts directly.
  return new File([header, file], file.name, {
    type: file.type,
    lastModified: file.lastModified,
  });
}
